import { Overlay } from "@angular/cdk/overlay";
import { AfterViewInit, Component, HostListener, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { Observable, Subject, Subscription, timer } from "rxjs";
import { debounceTime, map, take, tap } from "rxjs/operators";
import { DraftCardDto, WasteRegisterPublicApiApiModelsResponsesWasteRegisterWasteTransferCardV1KpoAsCarrierSearchResultDto, WasteTransferCardDto, WasteTransferCardService } from "../../../../../api";
import { DraftCard } from "../../../../../api/model/draftCard";
import { AlertService, MessageType } from "../../../services/alert.service";
import { CardService } from "../../../services/card.service";
import { MessagingService } from "../../../services/messaging.service";
import { CardExportJob, CardExportService, CardExportStatus, CreateCardExportRequest } from "../../../services/card-export.service";
import { BdoFilterComponent } from "../../bdo-filter/bdo-filter.component";
import { FilingConfirmationDialogComponent } from "../../dialogs/filing/filing-confirmation-dialog/filing-confirmation-dialog.component";
import { LOCAL_STORAGE, WebStorageService } from "ngx-webstorage-service";
import { FiledCardDetailsComponent } from "../../dialogs/filed-card-details/filed-card-details.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

type KpoCard = WasteRegisterPublicApiApiModelsResponsesWasteRegisterWasteTransferCardV1KpoAsCarrierSearchResultDto;

@UntilDestroy()
@Component({
  selector: "app-waste-transfer-cards",
  templateUrl: "./waste-transfer-cards.component.html",
  styleUrls: ["./waste-transfer-cards.component.scss"],
})
export class WasteTransferCardsComponent implements OnInit, AfterViewInit {
  loading = true;
  error = false;

  reloadRequest: Subject<void> = new Subject();
  refreshSubscription = new Subscription();
  requestSubscription = new Subscription();
  scrollStrategy = this.overlay.scrollStrategies.block();
  currentPageSize = 0;

  filteredResults = [];
  filteredResults$: Observable<any[]> = new Observable((observer) => {
    this.paginator.page.pipe(untilDestroyed(this)).subscribe((p) => {
      this.load(observer);
    });
    this.load(observer);
    this.handleQueryChanged(observer);
    this.handleRefresh(observer);
    this.reloadRequest.pipe(untilDestroyed(this)).subscribe(() => this.load(observer));
  });

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(BdoFilterComponent, { static: true })
  bdoFilter: BdoFilterComponent;
  isWorking: boolean;
  totalItemCount = 0;

  exports: CardExportJob[] = [];
  readonly exportStatus = CardExportStatus;
  private exportsLoaded = false;

  constructor(public cardService: CardService, private alertService: AlertService, private messagingService: MessagingService, public dialog: MatDialog, private overlay: Overlay, private wasteTransferCardService: WasteTransferCardService, @Inject(LOCAL_STORAGE) private storage: WebStorageService, private cardExportService: CardExportService) {}

  ngAfterViewInit(): void {
    this.currentPageSize = this.paginator.pageSize;
    this.filteredResults$.subscribe((filteredResults) => (this.filteredResults = filteredResults));
    this.bdoFilter.advancedQuery.additionalInfo = "";

    timer(0, 3000)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (!this.exportsLoaded || this.hasActiveExports()) this.loadExports();
      });
  }

  ngOnInit(): void {
    let currentDate = new Date();
    let startDate = new Date(currentDate.valueOf() - 1000 * 60 * 60 * 24 * 14);
    this.bdoFilter.advancedQuery.transportDateRange.from = startDate;
    this.bdoFilter.advancedQuery.transportDateRange.to = currentDate;
  }

  load(observer) {
    this.error = false;
    this.loading = true;

    let skip = this.paginator.pageIndex * this.paginator.pageSize;
    let take = this.paginator.pageSize;

    this.wasteTransferCardService
      .getFilteredWasteTransferCards(
        {
          cardNumber: this.bdoFilter.advancedQuery.cardNumber,
          cardStatusCodeName: this.bdoFilter.advancedQuery.cardStatusCodeName,
          startTransportDate: this.bdoFilter.advancedQuery.transportDateRange.from,
          endTransportDate: this.bdoFilter.advancedQuery.transportDateRange.to,
          registrationNumber: this.bdoFilter.advancedQuery.vehicleRegistrationNumber,
          senderName: this.bdoFilter.advancedQuery.senderName,
          wasteCode: this.bdoFilter.advancedQuery.wasteCode,
          skip: skip,
          take: take,
          additionalInfo: this.bdoFilter.advancedQuery.additionalInfo,
          year: this.getYearFilter(),
        },
        undefined,
        undefined,
        this.storage.get("eupId"),
        true
      )
      .subscribe(
        (result) => {
          observer.next(result.items);
          this.paginator.length = result.totalItemCount;
          this.totalItemCount = result.totalItemCount;
          this.loading = false;
          this.error = false;
        },
        () => {
          this.error = true;
          this.loading = false;
          this.alertService.showMessage("Wystąpił błąd podczas ładowania listy", MessageType.Error);
        }
      );
  }

  handleRefresh(observer) {
    this.messagingService.messageReceived$.pipe(untilDestroyed(this)).subscribe((payload) => {
      this.alertService.showMessage(payload.data.body.replace("\r\n", " | "), MessageType.Notification);
      this.load(observer);
    });
  }

  handleQueryChanged(observer) {
    this.bdoFilter.advancedQueryTextChanged.pipe(untilDestroyed(this), debounceTime(300)).subscribe(() => {
      this.load(observer);
    });

    this.bdoFilter.advancedQueryChanged.pipe(untilDestroyed(this)).subscribe(() => {
      this.load(observer);
    });
  }

  @HostListener("window:focus", ["$event"])
  onFocus(event: FocusEvent): void {
    this.reloadRequest.next();
  }

  openDetailsDialog(card): void {
    this.dialog.open(FiledCardDetailsComponent, {
      width: "950px",
      data: { cardId: card.id },
      scrollStrategy: this.scrollStrategy,
      closeOnNavigation: true,
    });
  }

  selectedCount(): number {
    return this.filteredResults.filter((r) => r?.checked).length;
  }

  selectAll() {
    this.filteredResults.forEach((element) => {
      element.checked = true;
    });
  }

  exportSelected() {
    const kpoIds = this.filteredResults.filter((r) => r?.checked).map((r) => r.kpoId);
    if (kpoIds.length === 0) return;
    this.createExport({ ...this.buildExportFilter(), kpoIds });
  }

  exportFiltered() {
    if (this.totalItemCount === 0) return;
    this.createExport(this.buildExportFilter());
  }

  cancelExport(job: CardExportJob) {
    this.cardExportService.cancel(job.id).subscribe(
      () => this.loadExports(),
      (error) => this.alertService.showMessage(this.errorText(error, "Nie udało się anulować eksportu"), MessageType.Error)
    );
  }

  downloadExport(job: CardExportJob) {
    this.cardExportService.download(job);
  }

  isActive(job: CardExportJob): boolean {
    return job.status === CardExportStatus.Pending || job.status === CardExportStatus.Running;
  }

  hasActiveExports(): boolean {
    return this.exports.some((e) => this.isActive(e));
  }

  progress(job: CardExportJob): number {
    return job.totalCount ? Math.round((job.processedCount / job.totalCount) * 100) : 0;
  }

  statusLabel(job: CardExportJob): string {
    switch (job.status) {
      case CardExportStatus.Pending:
        return "Oczekuje w kolejce";
      case CardExportStatus.Running:
        return `Pobieranie ${job.processedCount} z ${job.totalCount}`;
      case CardExportStatus.Completed:
        return job.failedCount > 0 ? `Gotowy - nie udało się pobrać ${job.failedCount} kart (lista w pliku bledy.txt)` : "Gotowy";
      case CardExportStatus.Failed:
        return "Błąd: " + (job.errorMessage || "nieznany");
      case CardExportStatus.Cancelled:
        return "Anulowany";
    }
  }

  fileSizeLabel(bytes: number): string {
    if (!bytes) return "";
    return bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;
  }

  private createExport(request: CreateCardExportRequest) {
    this.isWorking = true;
    this.cardExportService.create(request).subscribe(
      (job) => {
        this.isWorking = false;
        this.alertService.showMessage(`Zlecono eksport ${job.totalCount} kart. Postęp widać w sekcji "Eksporty", a w tym czasie można dalej korzystać z systemu.`, MessageType.Info);
        this.loadExports();
      },
      (error) => {
        this.isWorking = false;
        this.alertService.showMessage(this.errorText(error, "Nie udało się zlecić eksportu"), MessageType.Error);
      }
    );
  }

  private loadExports() {
    const wasActive = new Set(this.exports.filter((e) => this.isActive(e)).map((e) => e.id));
    this.cardExportService.getAll().subscribe(
      (exports) => {
        if (exports.some((e) => e.status === CardExportStatus.Completed && wasActive.has(e.id))) {
          this.alertService.showMessage("Eksport kart jest gotowy do pobrania", MessageType.Notification);
        }
        this.exports = exports;
        this.exportsLoaded = true;
      },
      () => (this.exportsLoaded = true)
    );
  }

  private buildExportFilter(): CreateCardExportRequest {
    const query = this.bdoFilter.advancedQuery;
    return {
      eupId: this.storage.get("eupId"),
      cardNumber: query.cardNumber,
      wasteCode: query.wasteCode,
      registrationNumber: query.vehicleRegistrationNumber,
      senderName: query.senderName,
      additionalInfo: query.additionalInfo,
      startTransportDate: query.transportDateRange.from,
      endTransportDate: query.transportDateRange.to,
      year: this.getYearFilter(),
    };
  }

  private getYearFilter(): number | undefined {
    const year = (this.bdoFilter.advancedQuery.year || "").trim();
    return /^\d{4}$/.test(year) ? parseInt(year, 10) : undefined;
  }

  private errorText(error: any, fallback: string): string {
    return typeof error?.error === "string" && error.error.length ? error.error : fallback;
  }
}
